import { create } from 'zustand';
import { FinancialRecord } from '../types/data/financialRecord';
import { analyzeData } from '../services/bedrockApi';

interface VisuAIDataViewerState {
  // Analysis state
  prompt: string;
  isAnalyzing: boolean;
  analysisResult: string | null;
  analysisError: string | null;
  
  // Actions
  setPrompt: (prompt: string) => void;
  analyzeData: (records: FinancialRecord[]) => Promise<void>;
  clearAnalysis: () => void;
}

// Hardcoded Claude model ID - use the Inference Profile ARN directly
const CLAUDE_37_SONNET_MODEL_ID = 'arn:aws:bedrock:eu-central-1::inference-profile/eu.anthropic.claude-3-7-sonnet-20250219-v1:0';

const useVisuAIDataViewerStore = create<VisuAIDataViewerState>((set, get) => ({
  // Initial state
  prompt: '',
  isAnalyzing: false,
  analysisResult: null,
  analysisError: null,
  
  // Set prompt 
  setPrompt: (prompt) => {
    set({ prompt });
  },
  
  // Analyze data with Claude via Bedrock - using the same approach as AIAnalysisButton.tsx
  analyzeData: async (records) => {
    const prompt = get().prompt.trim();
    
    if (!prompt || records.length === 0) {
      set({ 
        analysisError: 'Please provide a question and ensure there is data to analyze',
        isAnalyzing: false
      });
      return;
    }
    
    try {
      set({ 
        isAnalyzing: true, 
        analysisError: null,
        analysisResult: null
      });
      
      console.log(`Starting analysis with ${records.length} records`);
      
      // Using the same method as AIAnalysisButton.tsx
      // Create headers and rows for the API
      const headers = [
        'id',
        'dealerCode',
        'dealerName',
        'year',
        'month',
        'brand',
        'groupId',
        'groupName',
        'newVehicleUnitsSold',
        'newVehicleSalesRevenue',
        'newVehicleGrossProfit',
        'usedVehicleUnitsSold',
        'usedVehicleSalesRevenue',
        'usedVehicleGrossProfit',
        'financeInsuranceIncome',
        'partsServiceRevenue',
        'partsServiceGrossProfit',
        'totalDirectExpenses',
        'personnelExpenses',
        'advertisingExpenses',
        'totalDirectProfit'
      ];
      
      // Format rows like in AIAnalysisButton
      const rows = records.map(record => {
        return headers.map(header => {
          const value = record[header as keyof FinancialRecord];
          return value !== undefined && value !== null ? String(value) : '';
        });
      });
      
      console.log('Fetching available AWS Bedrock models before analysis...');
      
      // Create the prompt for financial analysis
      const dataVisualizationPrompt = `You are an expert data visualization specialist with 20 years of experience.
You are analyzing financial data for a group of automotive dealerships to create visualizations only.

The data includes information about dealership performance across various metrics:
- Dealer code and name identification
- Dealer group affiliation and group name
- Vehicle sales volumes and revenues
- Gross profits across departments
- F&I (Finance & Insurance) income
- Parts & Service performance
- Various expense categories
- Bottom-line profitability

When responding, ONLY provide:
1. The exact data fields to visualize (specific column names from the dataset)
2. The type of chart to use (bar, line, pie, etc.)
3. Specific data transformations needed (e.g., sum by month, average by dealer)
4. Clear labels for axes, legends, and titles
5. Recommended color schemes if relevant

IMPORTANT:
- Don't provide explanations about visualization theory or general advice
- Don't explain why you chose a particular chart type
- Don't include any text not directly needed to create the visualization
- Be concise and specific - the response will be used to programmatically generate charts

User's question: ${prompt}

Respond with ONLY the specific requirements to generate the visualization.`;

      // Prepare the data in the same format used by AIAnalysisButton
      const dataForAPI = {
        prompt: dataVisualizationPrompt,
        data: {
          headers: headers,
          rows: rows,
          fileName: 'financial_records',
          fileType: 'csv'
        }
      };
      
      console.log(`Sending data to Claude 3.7 Sonnet (Inference Profile) for analysis`);
      
      // Use the same API call as in AIAnalysisButton
      const result = await analyzeData(dataForAPI);
      
      set({ 
        analysisResult: result,
        isAnalyzing: false
      });
    } catch (error: any) {
      console.error('Error analyzing data:', error);
      
      // Extract a more user-friendly error message
      let errorMessage = 'Failed to analyze data';
      
      if (error instanceof Error) {
        errorMessage = error.message;
        
        // Handle specific error cases like in AIAnalysisButton
        if (error.message?.includes('too large') || 
            error.message?.includes('token limit') ||
            error.message?.includes('exceeds maximum size')) {
          errorMessage = 'Dataset is too large for analysis. Please apply more filters to reduce the dataset size.';
        } else if (errorMessage.includes('security token') || 
            errorMessage.includes('credentials') || 
            errorMessage.includes('authentication')) {
          errorMessage = 'AWS authentication failed. Please contact your administrator.';
        } else if (errorMessage.includes('not authorized') || errorMessage.includes('permission')) {
          errorMessage = 'Your AWS account does not have permission to use this service. Please contact your administrator.';
        }
      }
      
      set({ 
        isAnalyzing: false, 
        analysisError: errorMessage
      });
    }
  },
  
  // Clear analysis results
  clearAnalysis: () => {
    set({
      analysisResult: null,
      analysisError: null
    });
  }
}));

export default useVisuAIDataViewerStore; 